import type { ITelemetryService } from '@/service/vpc/types/ITelemetryService';
import type { AccountInfo } from '@azure/msal-browser';

import { countries } from '@/utils/country';

// Mirrors the backend market resolution (MarketConstants): ISO country -> legal-contract market.
const EU_ISO = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'NO', 'IS', 'LI',
  'CH',
]);

// Token-claim country shapes the countries list doesn't carry (mirrors backend
// CountryNameToIso): aliases resolve before the 2-letter passthrough so "UK" -> GB.
const CLAIM_ISO_ALIASES: Record<string, string> = {
  usa: 'US',
  uk: 'GB',
  'great britain': 'GB',
  czechia: 'CZ',
};

export const marketForCountry = (country: string | null | undefined): string | null => {
  if (!country) return null;
  const iso =
    CLAIM_ISO_ALIASES[country.toLowerCase()] ??
    (country.length === 2
      ? country.toUpperCase()
      : countries.find((c) => c.name.toLowerCase() === country.toLowerCase())?.code);
  if (!iso) return null;
  if (EU_ISO.has(iso)) return 'EU';
  if (iso === 'US' || iso === 'CA') return 'US';
  if (iso === 'BR') return 'BR';
  if (iso === 'AU') return 'AU';
  if (iso === 'GB') return 'UK';
  return null;
};

export const retrieveCountry = (account: AccountInfo | null): string | null => {
  const claims = account?.idTokenClaims as Record<string, unknown> | undefined;
  return typeof claims?.country === 'string' ? claims.country : null;
};

const IN_FLIGHT: ReadonlySet<ITelemetryService['status']> = new Set([
  'Active',
  'Requested',
  'Deactivating requested',
]);

// ponytail: client-side mirror of the backend market collapse (bug 1539968). The backend
// may return one row per (serviceName, market clone); keep one row per serviceName:
// an in-flight subscription first (never hide it), then the user's market row, then the
// lowest eligible serviceId, then the lowest serviceId. No-op once the backend collapse
// is serving; still guards against duplicate rows from the proxy's per-account merge.
export const dedupeServicesByName = (
  services: ITelemetryService[],
  userMarket: string | null
): ITelemetryService[] => {
  const groups = new Map<string, ITelemetryService[]>();
  for (const service of services) {
    const group = groups.get(service.serviceName) ?? [];
    group.push(service);
    groups.set(service.serviceName, group);
  }
  return [...groups.values()].map((group) => {
    const byId = [...group].sort((a, b) => a.serviceId - b.serviceId);
    return (
      byId.find((s) => s.individualServiceId != null && IN_FLIGHT.has(s.status)) ??
      byId.find((s) => userMarket !== null && s.serviceOffer?.market === userMarket) ??
      byId.find((s) => s.eligibility?.isEligible === true) ??
      byId[0]
    );
  });
};

// A card renders for every row that is not supported === false (services page filter).
export const hasVisibleServices = (services: ITelemetryService[] | null | undefined): boolean =>
  (services ?? []).some((s) => s.supported !== false);
